import React, { useState } from 'react';
import { 
  Heart, 
  Brain, 
  Activity, 
  Users, 
  Calendar, 
  FileText, 
  Plus, 
  Search, 
  Filter,
  Edit,
  Eye,
  Download,
  AlertCircle,
  CheckCircle,
  Clock,
  Target,
  Phone,
  Mail,
  Star,
  TrendingUp,
  TrendingDown,
  Smile,
  Frown
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';

interface WellbeingCheck {
  id: string;
  date: string;
  mood: number; // 1-10 scale
  energy: number;
  sleep: number;
  stress: number;
  notes: string;
}

interface CounsellingSession {
  id: string;
  therapist: string;
  date: string;
  time: string;
  type: string;
  duration: number;
  status: 'scheduled' | 'completed' | 'cancelled';
  notes: string;
}

interface WellnessGoal {
  id: string;
  title: string;
  category: string;
  targetDate: string;
  progress: number;
  status: 'in_progress' | 'completed' | 'not_started';
  description: string;
}

interface SupportContact {
  id: string;
  name: string;
  type: string;
  phone: string;
  email: string;
  availability: string;
  rating: number;
  isEmergency: boolean;
}

export function MobileWellbeingDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'mood' | 'counselling' | 'goals' | 'support'>('overview');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data
  const wellbeingChecks: WellbeingCheck[] = [
    {
      id: '1',
      date: '2024-01-25',
      mood: 8,
      energy: 7,
      sleep: 6,
      stress: 4,
      notes: 'Feeling good today, had a productive morning'
    },
    {
      id: '2',
      date: '2024-01-24',
      mood: 6,
      energy: 5,
      sleep: 7,
      stress: 6,
      notes: 'A bit tired but managing well'
    },
    {
      id: '3',
      date: '2024-01-23',
      mood: 9,
      energy: 8,
      sleep: 8,
      stress: 3,
      notes: 'Excellent day, felt very positive'
    }
  ];

  const counsellingSessions: CounsellingSession[] = [
    {
      id: '1',
      therapist: 'Dr. Sarah Johnson',
      date: '2024-02-01',
      time: '2:00 PM',
      type: 'Cognitive Behavioral Therapy',
      duration: 60,
      status: 'scheduled'
    },
    {
      id: '2',
      therapist: 'Dr. Michael Chen',
      date: '2024-01-20',
      time: '10:00 AM',
      type: 'Stress Management',
      duration: 45,
      status: 'completed',
      notes: 'Discussed work-life balance strategies'
    },
    {
      id: '3',
      therapist: 'Dr. Emily Davis',
      date: '2024-01-15',
      time: '3:30 PM',
      type: 'Anxiety Support',
      duration: 60,
      status: 'completed',
      notes: 'Progress on breathing techniques'
    }
  ];

  const wellnessGoals: WellnessGoal[] = [
    {
      id: '1',
      title: 'Practice Mindfulness Daily',
      category: 'Mental Health',
      targetDate: '2024-06-30',
      progress: 75,
      status: 'in_progress',
      description: 'Meditate for 10 minutes each morning'
    },
    {
      id: '2',
      title: 'Exercise 3 Times Weekly',
      category: 'Physical Health',
      targetDate: '2024-12-31',
      progress: 60,
      status: 'in_progress',
      description: 'Cardio and strength training sessions'
    },
    {
      id: '3',
      title: 'Improve Sleep Quality',
      category: 'Sleep',
      targetDate: '2024-03-31',
      progress: 40,
      status: 'in_progress',
      description: 'Establish consistent bedtime routine'
    }
  ];

  const supportContacts: SupportContact[] = [
    {
      id: '1',
      name: 'Lifeline Australia',
      type: 'Crisis Support',
      phone: '13 11 14',
      email: 'info@lifeline.org.au',
      availability: '24/7',
      rating: 4.9,
      isEmergency: true
    },
    {
      id: '2',
      name: 'Beyond Blue',
      type: 'Mental Health Support',
      phone: '1300 22 4636',
      email: 'info@beyondblue.org.au',
      availability: '24/7',
      rating: 4.8,
      isEmergency: false
    },
    {
      id: '3',
      name: 'Dr. Sarah Johnson',
      type: 'Counsellor',
      phone: '(555) 123-4567',
      email: 'sarah.johnson@therapy.com',
      availability: 'Mon-Fri 9AM-5PM',
      rating: 4.7,
      isEmergency: false
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
      case 'in_progress':
        return 'text-blue-600 bg-blue-100';
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'cancelled':
      case 'not_started':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getMoodIcon = (mood: number) => {
    if (mood >= 8) return <Smile size={16} className="text-green-500" />;
    if (mood >= 6) return <Smile size={16} className="text-yellow-500" />;
    return <Frown size={16} className="text-red-500" />;
  };

  const getMoodColor = (mood: number) => {
    if (mood >= 8) return 'text-green-600';
    if (mood >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const averageMood = wellbeingChecks.reduce((sum, check) => sum + check.mood, 0) / wellbeingChecks.length;
  const averageEnergy = wellbeingChecks.reduce((sum, check) => sum + check.energy, 0) / wellbeingChecks.length;
  const averageSleep = wellbeingChecks.reduce((sum, check) => sum + check.sleep, 0) / wellbeingChecks.length;

  const renderOverview = () => (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Brain size={16} className="text-purple-500" />
            <h3 className="font-medium text-gray-900">Mood Score</h3>
          </div>
          <div className="flex items-center gap-2">
            {getMoodIcon(averageMood)}
            <p className={`text-2xl font-bold ${getMoodColor(averageMood)}`}>
              {averageMood.toFixed(1)}/10
            </p>
          </div>
          <p className="text-xs text-gray-500">7-day average</p>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity size={16} className="text-blue-500" />
            <h3 className="font-medium text-gray-900">Energy Level</h3>
          </div>
          <p className="text-2xl font-bold text-gray-900">{averageEnergy.toFixed(1)}/10</p>
          <p className="text-xs text-gray-500">7-day average</p>
        </Card>
      </div>

      {/* Sleep Tracking */}
      <Card className="p-4">
        <h3 className="font-medium text-gray-900 mb-3">Sleep Quality</h3>
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Average Hours:</span>
            <span className="font-medium">{averageSleep.toFixed(1)} hours</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-idecide-primary h-2 rounded-full" 
              style={{ width: `${(averageSleep / 10) * 100}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500">Target: 8 hours per night</p>
        </div>
      </Card>

      {/* Recent Mood Checks */}
      <div>
        <h3 className="font-medium text-gray-900 mb-3">Recent Mood Checks</h3>
        <div className="space-y-2">
          {wellbeingChecks.slice(0, 3).map((check) => (
            <div key={check.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {getMoodIcon(check.mood)}
                  <p className="font-medium text-gray-900">{check.date}</p>
                </div>
                <p className="text-sm text-gray-600">{check.notes}</p>
              </div>
              <div className="text-right">
                <p className={`font-medium ${getMoodColor(check.mood)}`}>{check.mood}/10</p>
                <p className="text-xs text-gray-500">Mood</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderMood = () => (
    <div className="space-y-3">
      {wellbeingChecks.map((check) => (
        <Card key={check.id} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {getMoodIcon(check.mood)}
                <h3 className="font-medium text-gray-900">{check.date}</h3>
              </div>
              <p className={`font-medium ${getMoodColor(check.mood)}`}>{check.mood}/10</p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <span className="text-gray-600">Energy:</span>
                <p className="font-medium">{check.energy}/10</p>
              </div>
              <div>
                <span className="text-gray-600">Sleep:</span>
                <p className="font-medium">{check.sleep}/10</p>
              </div>
              <div>
                <span className="text-gray-600">Stress:</span>
                <p className="font-medium">{check.stress}/10</p>
              </div>
            </div>
            {check.notes && (
              <p className="text-sm text-gray-600">{check.notes}</p>
            )}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1">
                <Eye size={14} className="mr-1" />
                View
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Edit size={14} className="mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderCounselling = () => (
    <div className="space-y-3">
      {counsellingSessions.map((session) => (
        <Card key={session.id} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{session.therapist}</h3>
                <p className="text-sm text-gray-600">{session.type}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(session.status)}`}>
                {session.status}
              </span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Date:</span>
                <span className="font-medium">{session.date} at {session.time}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Duration:</span>
                <span className="font-medium">{session.duration} minutes</span>
              </div>
              {session.notes && (
                <p className="text-sm text-gray-600">{session.notes}</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1">
                <Eye size={14} className="mr-1" />
                View
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Edit size={14} className="mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderGoals = () => (
    <div className="space-y-3">
      {wellnessGoals.map((goal) => (
        <Card key={goal.id} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{goal.title}</h3>
                <p className="text-sm text-gray-600">{goal.category}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(goal.status)}`}>
                {goal.status.replace('_', ' ')}
              </span>
            </div>
            <p className="text-sm text-gray-600">{goal.description}</p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Progress:</span>
                <span className="font-medium text-gray-900">{goal.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-idecide-primary h-2 rounded-full" 
                  style={{ width: `${goal.progress}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Target:</span>
                <span className="font-medium text-gray-900">{goal.targetDate}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1">
                <Eye size={14} className="mr-1" />
                View
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Edit size={14} className="mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderSupport = () => (
    <div className="space-y-3">
      {supportContacts.map((contact) => (
        <Card key={contact.id} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">{contact.name}</h3>
                  {contact.isEmergency && (
                    <span className="px-1 py-0.5 bg-red-100 text-red-600 text-xs rounded">Emergency</span>
                  )}
                </div>
                <p className="text-sm text-gray-600">{contact.type}</p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1">
                  <Star size={14} className="text-yellow-500" />
                  <span className="text-sm font-medium">{contact.rating}</span>
                </div>
                <p className="text-xs text-gray-500">Rating</p>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone size={14} />
                <span>{contact.phone}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail size={14} />
                <span>{contact.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock size={14} />
                <span>{contact.availability}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1">
                <Phone size={14} className="mr-1" />
                Call
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Edit size={14} className="mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="p-4 pb-safe space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Wellbeing Management</h2>
          <p className="text-sm text-gray-600">Track mental health, mood, and wellness goals</p>
        </div>
        <Button size="sm">
          <Plus size={16} className="mr-2" />
          Add Check
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search wellbeing..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
        />
      </div>

      {/* Tab Navigation */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant={activeTab === 'overview' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('overview')}
        >
          <Heart size={16} className="mr-2" />
          Overview
        </Button>
        <Button
          variant={activeTab === 'mood' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('mood')}
        >
          <Brain size={16} className="mr-2" />
          Mood
        </Button>
        <Button
          variant={activeTab === 'counselling' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('counselling')}
        >
          <Users size={16} className="mr-2" />
          Counselling
        </Button>
        <Button
          variant={activeTab === 'goals' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('goals')}
        >
          <Target size={16} className="mr-2" />
          Goals
        </Button>
        <Button
          variant={activeTab === 'support' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('support')}
        >
          <Phone size={16} className="mr-2" />
          Support
        </Button>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'mood' && renderMood()}
        {activeTab === 'counselling' && renderCounselling()}
        {activeTab === 'goals' && renderGoals()}
        {activeTab === 'support' && renderSupport()}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button variant="outline" className="w-full">
          <Brain size={16} className="mr-2" />
          Mood Check
        </Button>
        <Button variant="outline" className="w-full">
          <Users size={16} className="mr-2" />
          Book Session
        </Button>
      </div>

      {/* Emergency Support */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle size={16} className="text-red-600" />
          <h4 className="font-medium text-red-800">Emergency Support</h4>
        </div>
        <div className="space-y-1 text-sm text-red-700">
          <p>• Lifeline: 13 11 14 (24/7)</p>
          <p>• Beyond Blue: 1300 22 4636</p>
        </div>
      </div>
    </div>
  );
} 